import {
    instance as axiosInstance,
    withHeaders,
    getLifeGraphQueryCustomHeaders,
} from 'core/util';
import { AxiosResponse } from 'axios';
import { useMutation, UseMutationResult, MutationFunction } from 'react-query';
import { useAlert } from 'core/contexts';
import { commonShowAlert, saveCurrentQuery } from 'lib';
import { LifeGraphExplorerFilter } from '../pages/lifegraph-explorer/components/lifegraph-explorer/lifegraph-explorer.types';

interface PostGraphChainProps {
    filter: LifeGraphExplorerFilter | string;
    token: string;
    saveQueries?: boolean;
}

interface ValidateGraphChainQueryProps {
    query: string;
    token: string;
}
export const validateGraphChainQuery = async (
    props: ValidateGraphChainQueryProps
): Promise<AxiosResponse<any>> => {
    const { query, token } = props;
    const url = '/graphchain/query/validate/graph';

    return axiosInstance().post(
        url,
        query,
        withHeaders(getLifeGraphQueryCustomHeaders(token))
    );
};

const postGraphChain: MutationFunction<
    AxiosResponse<any>,
    PostGraphChainProps
> = async (props: PostGraphChainProps) => {
    const { filter, token, saveQueries = true } = props;
    const url = `/graphchain/query`;
    let query: string;
    if (typeof filter === 'string') {
        query = filter;

        if (saveQueries) {
            saveCurrentQuery('graphQueries', query);
        }

        // We are disabling all query validations for now.
        return axiosInstance().post(
            url,
            query,
            withHeaders(getLifeGraphQueryCustomHeaders(token))
        );
    } else {
        query = `GRAPH ${filter.chain} ${
            filter.attributeKey &&
            filter.attributeValue &&
            filter.attributeKey !== 'None'
                ? `WHERE data.${filter.attributeKey} == ${filter.attributeValue}`
                : ''
        } LIMIT ${filter.limit} TRAVERSE ANY 0..${filter.depth}`;

        return axiosInstance().post(
            url,
            query,
            withHeaders(getLifeGraphQueryCustomHeaders(token))
        );
    }
};

export const useGraphChain = (): UseMutationResult<
    AxiosResponse<any>,
    unknown,
    PostGraphChainProps
> => {
    const { showAlert, hideAlert, showErrorDetails } = useAlert();

    return useMutation(postGraphChain, {
        onError: (error: any) => {
            const errorMessage: string =
                error?.data?.message ?? 'Failed to fetch data';
            commonShowAlert(
                showAlert,
                hideAlert,
                'error',
                errorMessage,
                JSON.stringify(error?.data ?? ''),
                showErrorDetails
            );
        },
    });
};

export const useValidateGraphChain = (): UseMutationResult<
    AxiosResponse<any>,
    unknown,
    ValidateGraphChainQueryProps
> => {
    const { showAlert, hideAlert, showErrorDetails } = useAlert();

    return useMutation(validateGraphChainQuery, {
        onError: (error: any) => {
            const errorMessage: string =
                error?.data?.message ??
                'Unable to download TQL query due to a syntax error';
            commonShowAlert(
                showAlert,
                hideAlert,
                'error',
                errorMessage,
                JSON.stringify(error?.data ?? ''),
                showErrorDetails
            );
        },
    });
};
